import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ExclamationTriangleIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const noop = () => {};

const DeleteVisionModal = ({
  vision,
  onClose = noop,
  onConfirm = noop,
  isDeleting = false,
}) => {
  const [confirmationText, setConfirmationText] = useState("");
  const [localDeleting, setLocalDeleting] = useState(false);
  const [error, setError] = useState("");
  const overlayRef = useRef(null);
  const inputRef = useRef(null);

  const targetTitle = vision?.title || "";

  const isConfirmed = useMemo(
    () => confirmationText === targetTitle && targetTitle.length > 0,
    [confirmationText, targetTitle]
  );

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !isDeleting && !localDeleting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, isDeleting, localDeleting]);

  const onOverlayClick = (e) => {
    if (e.target === overlayRef.current && !isDeleting && !localDeleting) {
      onClose();
    }
  };

  const handleConfirm = useCallback(async () => {
    if (!isConfirmed || isDeleting || localDeleting) return;
    setError("");
    setLocalDeleting(true);
    try {
      const maybe = onConfirm(vision);
      if (maybe && typeof maybe.then === "function") {
        await maybe;
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to delete the vision. Please try again.";
      setError(msg);
      setLocalDeleting(false);
      return;
    }
    setLocalDeleting(false);
  }, [isConfirmed, isDeleting, localDeleting, onConfirm, vision]);

  const busy = isDeleting || localDeleting;

  return (
    <AnimatePresence>
      <motion.div
        ref={overlayRef}
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-title"
      >
        <motion.div
          className="surface-panel max-w-md w-full"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
        >
          <div className="flex items-center justify-between p-6 border-b border-hairline">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-pine-tint rounded-full flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-danger" />
              </div>
              <div>
                <h2 id="delete-title" className="text-xl font-display font-semibold text-primary">
                  Delete Vision
                </h2>
                <p className="text-secondary text-sm">This action cannot be undone.</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="btn-ghost p-1 disabled:opacity-50"
              disabled={busy}
              aria-label="Close"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6">
            <div className="border border-danger/40 bg-pine-tint rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-danger mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-danger font-semibold mb-2">Warning</h3>
                  <p className="text-secondary text-sm">
                    You're about to permanently delete <strong className="text-primary">"{targetTitle}"</strong> and all associated data:
                  </p>
                  <ul className="text-secondary text-sm mt-2 space-y-1 ml-1">
                    <li>• Journal entries</li>
                    <li>• Chat messages</li>
                    <li>• Uploaded files</li>
                    <li>• Progress tracking data</li>
                  </ul>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg border border-danger/40 bg-pine-tint text-danger text-sm">
                {error}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-primary font-medium mb-2">
                To confirm, type the vision title exactly:
              </label>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 surface-panel p-3 overflow-x-auto">
                  <code className="text-secondary text-sm break-words">{targetTitle}</code>
                </div>
                <button
                  type="button"
                  className="btn-secondary px-3 py-2 text-xs"
                  onClick={() => {
                    navigator.clipboard?.writeText(targetTitle).catch(() => {});
                    inputRef.current?.focus();
                  }}
                  disabled={!targetTitle}
                  title="Copy title"
                >
                  Copy
                </button>
              </div>
              <input
                ref={inputRef}
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="Type the vision title to confirm"
                className={`input-editorial ${
                  isConfirmed ? "border-pine ring-pine" : "border-danger/40"
                }`}
                disabled={busy}
                aria-invalid={!isConfirmed}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isConfirmed && !busy) handleConfirm();
                }}
              />
              <p className={`text-xs mt-2 ${isConfirmed ? "text-pine" : "text-faint"}`}>
                {isConfirmed ? "Title matches." : "Case-sensitive. Must match exactly."}
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="btn-secondary flex-1"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!isConfirmed || busy}
                className="btn-danger flex-1 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? (
                  <>
                    <div className="w-4 h-4 border-2 border-hairline border-t-danger rounded-full animate-spin" />
                    <span>Deleting…</span>
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    <span>Delete Vision</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DeleteVisionModal;
